import React, { useState } from 'react';
import { Layout, Card, Row, Col, Typography, Spin, Statistic, DatePicker, Button, Space, message } from 'antd';
import { 
    TeamOutlined, 
    FilePdfOutlined,
    FileExcelOutlined,
    SearchOutlined,
    ClearOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ReportsPDF from './ReportsPDF';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';

const { Content } = Layout;
const { Title: AntTitle } = Typography;
const { RangePicker } = DatePicker;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const MemberReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [memberData, setMemberData] = useState(null);
    const [dateRange, setDateRange] = useState([null, null]);

    const fetchReport = async () => {
        setLoading(true);
        try {
            let queryParams = "";
            if (dateRange[0] && dateRange[1]) {
                const start = dateRange[0].format('YYYY-MM-DD');
                const end = dateRange[1].format('YYYY-MM-DD');
                queryParams = `?startDate=${start}&endDate=${end}`;
            }

            const res = await axiosClient.get(`/reports/get_member_reports.php${queryParams}`);
            
            if (res.data.status === 'success') {
                setMemberData(res.data.data);
                message.success("Member report generated.");
            }
        } catch (error) {
            console.error("Error fetching member report:", error);
            message.error("Failed to fetch report.");
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setDateRange([null, null]);
        setMemberData(null);
    };

    const handleExportExcel = () => {
        if (!memberData) return;
        const wb = XLSX.utils.book_new();
        const sheetData = memberData.monthlyRegistrations.map(item => ({
            Month: item.month,
            Registrations: item.count
        }));
        const ws = XLSX.utils.json_to_sheet(sheetData);
        XLSX.utils.book_append_sheet(wb, ws, "Member Growth");
        
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'});
        saveAs(data, "MemberReport.xlsx");
    };

    const statusChartData = {
        labels: ['Active', 'Inactive'],
        datasets: [
            {
                data: [
                    memberData?.statusDistribution.active || 0,
                    memberData?.statusDistribution.inactive || 0
                ],
                backgroundColor: ['rgba(54, 162, 235, 0.6)', 'rgba(255, 99, 132, 0.6)'],
                borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
                borderWidth: 1,
            },
        ],
    };

    const growthChartData = {
        labels: memberData?.monthlyRegistrations.map(item => item.month) || [],
        datasets: [
            {
                label: 'New Registrations',
                data: memberData?.monthlyRegistrations.map(item => item.count) || [],
                backgroundColor: 'rgba(153, 102, 255, 0.6)',
            }
        ]
    };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Headerdiv />
            <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
                <div style={{ marginBottom: 20 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <AntTitle level={2} style={{ margin: 0 }}><TeamOutlined /> Member Analytics</AntTitle>
                    <Space>
                        <RangePicker onChange={setDateRange} value={dateRange} />
                        <Button type="primary" icon={<SearchOutlined />} onClick={fetchReport} loading={loading}>Fetch</Button>
                        <Button icon={<ClearOutlined />} onClick={handleClear} disabled={loading}>Clear</Button>
                        
                        {memberData && (
                            <>
                                <PDFDownloadLink
                                    document={<ReportsPDF member={memberData} dateRange={dateRange} />}
                                    fileName="MemberReport.pdf"
                                >
                                    {({ loading: pdfLoading }) => (
                                        <Button danger icon={<FilePdfOutlined />} loading={pdfLoading}>PDF</Button>
                                    )}
                                </PDFDownloadLink>

                                <Button 
                                    onClick={handleExportExcel}
                                    style={{ borderColor: '#217346', color: '#217346' }} 
                                    icon={<FileExcelOutlined />}
                                >
                                    Excel
                                </Button>
                            </>
                        )}
                    </Space>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', marginTop: 50 }}><Spin size="large" /></div>
                ) : memberData ? (
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Card>
                                        <Statistic 
                                            title="Total Members" 
                                            value={memberData.totalMembers} 
                                            prefix={<TeamOutlined />}
                                        />
                                    </Card>
                                </Col>
                                <Col span={8}>
                                    <Card>
                                        <Statistic 
                                            title="Active Members" 
                                            value={memberData.statusDistribution.active} 
                                            valueStyle={{ color: '#3f8600' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card title="Member Status Distribution">
                                 <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
                                    <Doughnut data={statusChartData} />
                                 </div>
                            </Card>
                        </Col>
                        <Col xs={24} md={12}>
                            <Card title="New Registrations">
                                <Bar options={{ responsive: true }} data={growthChartData} />
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <div style={{ textAlign: 'center', marginTop: 100, color: '#999' }}>
                        <TeamOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <p>No report loaded.</p>
                    </div>
                )}
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default MemberReport;
